import { supabase } from "@/shared/lib/supabaseClient";
import type { ModuloVisibilidade } from "@/shared/types/database.types";

/**
 * Camada de dados da tela "Módulos" (Núcleo, só admin — 03-Núcleo §3):
 * ativação por igreja, visibilidade no menu e ministério responsável.
 *
 * Modelo (migration 0016): `modulos` é o catálogo da plataforma (definido
 * pelos desenvolvedores); `igreja_modulos` é a configuração DESTA igreja.
 * A visibilidade é separada da permissão (DA-002): quem enxerga ≠ quem
 * gerencia. A RLS restringe a escrita ao admin da plataforma.
 */

/** 'compartilhado' = todo ministério vinculado vê/gere tudo do módulo; 'individual' = cada um só vê o seu. Só importa com 2+ ministérios (migration 0030). */
export type ModoDadosModulo = "compartilhado" | "individual";

export interface ModuloIgreja {
  codigo: string;
  nome: string;
  descricao: string | null;
  /** false = módulo do catálogo ainda sem registro em igreja_modulos. */
  configurado: boolean;
  ativo: boolean;
  visibilidade: ModuloVisibilidade;
  /** Ministérios responsáveis pela gestão (0, 1 ou vários — migration 0027). */
  ministerioIds: string[];
  ministerioNomes: string[];
  /** Só é decisão real quando ministerioIds.length > 1 (migration 0030). */
  modoDados: ModoDadosModulo;
}

export interface OpcaoMinisterio {
  id: string;
  nome: string;
}

export interface ModulosIgrejaDados {
  modulos: ModuloIgreja[];
  /** Ministérios ativos — opções para "ministério responsável". */
  ministerios: OpcaoMinisterio[];
}

export interface ConfiguracaoModulo {
  ativo: boolean;
  visibilidade: ModuloVisibilidade;
  /** Ministérios responsáveis (substitui o antigo ministerioId único). */
  ministerioIds: string[];
  /** Só tem efeito com 2+ ministerioIds — migration 0030. */
  modoDados: ModoDadosModulo;
}

export const igrejaModulosService = {
  async listar(): Promise<ModulosIgrejaDados> {
    const [catalogoRes, ativacoesRes, responsaveisRes, ministeriosRes] = await Promise.all([
      supabase.from("modulos").select("codigo, nome, descricao").order("nome"),
      supabase.from("igreja_modulos").select("modulo_codigo, ativo, visibilidade, modo_dados"),
      supabase.from("igreja_modulos_ministerios").select("modulo_codigo, ministerio_id"),
      supabase.from("ministerios").select("id, nome").eq("ativo", true).order("nome"),
    ]);
    if (catalogoRes.error || ativacoesRes.error || responsaveisRes.error || ministeriosRes.error) {
      throw new Error("Não foi possível carregar os elos.");
    }
    const catalogo = catalogoRes.data ?? [];
    const ativacoes = ativacoesRes.data ?? [];
    const responsaveis = responsaveisRes.data ?? [];
    const ministerios = ministeriosRes.data ?? [];
    const nomeMinisterio = new Map(ministerios.map((m) => [m.id, m.nome]));

    const modulos: ModuloIgreja[] = catalogo.map((m) => {
      const cfg = ativacoes.find((a) => a.modulo_codigo === m.codigo);
      const idsResponsaveis = responsaveis
        .filter((r) => r.modulo_codigo === m.codigo)
        .map((r) => r.ministerio_id);
      return {
        codigo: m.codigo,
        nome: m.nome,
        descricao: m.descricao,
        configurado: !!cfg,
        ativo: cfg?.ativo ?? false,
        visibilidade: (cfg?.visibilidade ?? "todos_membros") as ModuloVisibilidade,
        modoDados: (cfg?.modo_dados ?? "compartilhado") as ModoDadosModulo,
        ministerioIds: idsResponsaveis,
        ministerioNomes: idsResponsaveis
          .map((id) => nomeMinisterio.get(id))
          .filter((n): n is string => !!n)
          .sort((a, b) => a.localeCompare(b)),
      };
    });

    return { modulos, ministerios };
  },

  /**
   * Cria ou atualiza a configuração do módulo para a igreja, incluindo a
   * lista de ministérios responsáveis (substitui-a por completo — apaga e
   * recria os vínculos, simples e suficiente para o volume desta tela).
   */
  async salvar(codigo: string, cfg: ConfiguracaoModulo): Promise<void> {
    const { data: existente, error: erroBusca } = await supabase
      .from("igreja_modulos")
      .select("igreja_id")
      .eq("modulo_codigo", codigo)
      .maybeSingle();
    if (erroBusca) throw new Error("Não foi possível verificar a configuração do elo.");

    let igrejaId = existente?.igreja_id;

    if (existente) {
      const { error } = await supabase
        .from("igreja_modulos")
        .update({ ativo: cfg.ativo, visibilidade: cfg.visibilidade, modo_dados: cfg.modoDados })
        .eq("modulo_codigo", codigo)
        .eq("igreja_id", existente.igreja_id);
      if (error) throw new Error("Não foi possível salvar a configuração do elo.");
    } else {
      const { data: igreja, error: erroIgreja } = await supabase
        .from("igrejas")
        .select("id")
        .limit(1)
        .single();
      if (erroIgreja || !igreja) throw new Error("Não foi possível identificar a igreja.");
      igrejaId = igreja.id;

      const { error } = await supabase.from("igreja_modulos").insert({
        igreja_id: igreja.id,
        modulo_codigo: codigo,
        ativo: cfg.ativo,
        visibilidade: cfg.visibilidade,
        modo_dados: cfg.modoDados,
      });
      if (error) throw new Error("Não foi possível salvar a configuração do elo.");
    }

    const { error: erroLimpar } = await supabase
      .from("igreja_modulos_ministerios")
      .delete()
      .eq("igreja_id", igrejaId as string)
      .eq("modulo_codigo", codigo);
    if (erroLimpar) throw new Error("Não foi possível atualizar os ministérios responsáveis.");

    if (cfg.ministerioIds.length) {
      const { error: erroInserir } = await supabase.from("igreja_modulos_ministerios").insert(
        cfg.ministerioIds.map((ministerioId) => ({
          igreja_id: igrejaId as string,
          modulo_codigo: codigo,
          ministerio_id: ministerioId,
        })),
      );
      if (erroInserir) throw new Error("Não foi possível atualizar os ministérios responsáveis.");
    }
  },
};
